import * as React from 'react';

import { useLockFn, useMemoizedFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useProductAction, useProductValue } from 'context';
import { createProduct, updateProduct } from 'services';
import {
  generateUniqueElements,
  localizeProductType,
  normalizeToString,
  PATHNAME,
  replaceExtraSeparator,
} from 'utils';

import { ProductFormAction } from './action';
import { ProductFormTabs } from './tabs';

const {
  LOCAL: {
    PRODUCTS: { BASE_URL: PRODUCTS_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<
  ReturnType<typeof useProductValue>,
  'isRecommended' | 'callForPrice' | 'showPostingStatus' | 'status'
> &
  Partial<Record<string, string | string[]>>;

const ProductForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    name = '',
    identifier = '',
    slug,
    brand,
    mainCategory,
    breadcrumbCategory,
    otherCategories = [],
    tags = [],
    complements = [],
    variants = [],
    isRecommended = false,
    callForPrice = false,
    showPostingStatus = false,
    description = '',
    productType,
    variableAttributes = [],
    subProducts = [],
    baseAttributes = [],
    packagingInfo = [],
    pictures = [],
    documents = [],
    seo,
    defaultName,
    defaultStatus,
    mode,
    step,
  } = useProductValue();
  const action = useProductAction();
  const navigate = useNavigate();

  const formatedIdentifier = replaceExtraSeparator(identifier);
  const formatedSlug = replaceExtraSeparator(slug ?? name);

  const handleUpdateProductBaseInfo = useMemoizedFn(
    ({
      isRecommended,
      callForPrice,
      showPostingStatus,
    }: Pick<
      FieldType,
      'isRecommended' | 'callForPrice' | 'showPostingStatus'
    >) => {
      action({
        type: 'UPDATE_PRODUCT_BASE_INFO',
        payload: {
          isRecommended,
          callForPrice,
          showPostingStatus,
        },
      });
      action({
        type: 'UPDATE_PRODUCT_STEP',
        payload: {
          step: 2,
        },
      });
    },
  );

  const handleUpdateProductAttributesInfo = useMemoizedFn(
    (attributes: Partial<Record<string, string | number | string[]>>) => {
      action({
        type: 'UPDATE_PRODUCT_BASE_ATTRIBUTES',
        payload: {
          baseAttributes: baseAttributes.map(({ id, ...otherAttributes }) => {
            const selectedValues = attributes[`base-attribute-${id}`] ?? [];

            return {
              ...otherAttributes,
              id,
              selectedValues: Array.isArray(selectedValues)
                ? selectedValues
                : [normalizeToString(selectedValues)],
            };
          }),
        },
      });
      action({
        type: 'UPDATE_PRODUCT_STEP',
        payload: {
          step: 3,
        },
      });
    },
  );

  const handleUpdateProductModelInfo = useMemoizedFn(() => {
    if (subProducts.length > 0) {
      action({
        type: 'UPDATE_PRODUCT_STEP',
        payload: {
          step: 4,
        },
      });
    } else {
      notification.warning({
        message: 'حداقل یک مدل باید موجود باشد.',
      });
    }
  });

  const handleUpdateProductFilesInfo = useMemoizedFn(
    (files: Partial<Record<string, string | string[]>>) => {
      if (pictures.length > 0 || documents.length > 0) {
        action({
          type: 'UPDATE_PRODUCT_PICTURES',
          payload: {
            pictures: pictures
              .map(picture => {
                const { id } = picture;
                const title = files[`file-title-${id}`] as string;
                const subProductId = files[`file-subProduct-${id}`] as string;
                const watermark = Boolean(files[`file-watermark-${id}`]);

                return {
                  ...picture,
                  title,
                  subProductId,
                  watermark,
                };
              })
              .sort((a, b) => {
                const aIndex = Number(files[`file-index-${a.id}`]);
                const bIndex = Number(files[`file-index-${b.id}`]);

                if (aIndex === bIndex) {
                  return pictures.indexOf(a) - pictures.indexOf(b);
                }
                return aIndex - bIndex;
              }),
          },
        });
        action({
          type: 'UPDATE_PRODUCT_DOCUMENTS',
          payload: {
            documents: documents
              .map(document => {
                const { id } = document;
                const title = files[`file-title-${id}`] as string;
                const subProductId = files[`file-subProduct-${id}`] as string;
                const watermark = Boolean(files[`file-watermark-${id}`]);

                return {
                  ...document,
                  title,
                  subProductId,
                  watermark,
                };
              })
              .sort((a, b) => {
                const aIndex = Number(files[`file-index-${a.id}`]);
                const bIndex = Number(files[`file-index-${b.id}`]);

                if (aIndex === bIndex) {
                  return documents.indexOf(a) - documents.indexOf(b);
                }
                return aIndex - bIndex;
              }),
          },
        });
        action({
          type: 'UPDATE_PRODUCT_STEP',
          payload: {
            step: 5,
          },
        });
      } else {
        notification.warning({
          message: 'حداقل یک مستند از محصول باید موجود باشد.',
        });
      }
    },
  );

  const handleUpdateProductPackagingInfo = useMemoizedFn(() => {
    if (packagingInfo.length > 0) {
      action({
        type: 'UPDATE_PRODUCT_STEP',
        payload: {
          step: 6,
        },
      });
    } else {
      notification.warning({
        message: 'حداقل یک بسته‌بندی از محصول باید موجود باشد.',
      });
    }
  });

  const handleUpdateProductVariants = useMemoizedFn(() => {
    action({
      type: 'UPDATE_PRODUCT_STEP',
      payload: {
        step: 7,
      },
    });
  });

  const handleUpdateProductComplements = useMemoizedFn(() => {
    action({
      type: 'UPDATE_PRODUCT_STEP',
      payload: {
        step: 8,
      },
    });
  });

  const handleUpdateProductSeoInfo = useLockFn(
    async ({ status = defaultStatus! }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updateProduct>[0] = {
        id,
      };
      const body: Parameters<typeof updateProduct>[1] = {
        name,
        identifier: formatedIdentifier,
        slug: formatedSlug,
        brandId: brand?.id,
        mainCategoryId: mainCategory?.id,
        breadcrumbCategoryId: breadcrumbCategory?.id,
        isRecommended,
        callForPrice,
        showPostingStatus,
        otherCategoriesIds: otherCategories.map(({ id }) => id),
        tagsIds: tags.map(({ id }) => id),
        description,
        baseAttributes: baseAttributes.map(({ id, selectedValues }) => ({
          id,
          selectedValues,
        })),
        productType: productType!,
        variableAttributesIds: variableAttributes.map(({ id }) => id),
        subProducts: subProducts.map(
          ({ priceHistories, attributes, ...otherSubProduct }) => ({
            ...otherSubProduct,
            attributes: attributes.map(({ id, selectedValues }) => ({
              id,
              selectedValues,
            })),
          }),
        ),
        pictures: pictures.map(({ id, title, subProductId, watermark }) => ({
          id,
          title,
          subProductId,
          watermark,
        })),
        documents: documents.map(({ id, title, subProductId, watermark }) => ({
          id,
          title,
          subProductId,
          watermark,
        })),
        packagingInfo: packagingInfo.map(
          ({
            identifier,
            packagingModel: { id: packagingModelId },
            measurementUnit: { id: measurementUnitId },
            amount,
          }) => ({
            identifier,
            packagingModelId,
            measurementUnitId,
            amount,
          }),
        ),
        variants: variants.map(({ product, ...otherVariant }) => ({
          ...otherVariant,
          productId: product.id,
        })),
        complementsIds: complements.map(({ mainSubProduct: { id } }) => id),
        seo,
        status,
      };

      (isUpdatedMode ? updateProduct(params, body) : createProduct(body))
        .then(() => {
          notification.success({
            message: `محصول ${isUpdatedMode ? defaultName : name} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(PRODUCTS_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  const onFinish = useMemoizedFn((fields: FieldType) => {
    const {
      isRecommended,
      callForPrice,
      showPostingStatus,
      status,
      ...otherValues
    } = fields;

    switch (step) {
      case 1: {
        return handleUpdateProductBaseInfo({
          isRecommended,
          callForPrice,
          showPostingStatus,
        });
      }
      case 2: {
        return handleUpdateProductAttributesInfo(otherValues);
      }
      case 3: {
        return handleUpdateProductModelInfo();
      }
      case 4: {
        return handleUpdateProductFilesInfo(otherValues);
      }
      case 5: {
        return handleUpdateProductPackagingInfo();
      }
      case 6: {
        return handleUpdateProductVariants();
      }
      case 7: {
        return handleUpdateProductComplements();
      }
      case 8: {
        return handleUpdateProductSeoInfo(fields);
      }
      default:
        break;
    }
  });

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'name',
          value: name,
        },
        {
          name: 'identifier',
          value: formatedIdentifier,
        },
        {
          name: 'slug',
          value: formatedSlug,
        },
        {
          name: 'brand',
          value: brand && {
            key: brand.id,
            label: brand.title,
            value: brand.id,
          },
        },
        {
          name: 'mainCategory',
          value: mainCategory && {
            key: mainCategory.id,
            label: mainCategory.title,
            value: mainCategory.id,
          },
        },
        {
          name: 'breadcrumbCategory',
          value: breadcrumbCategory && {
            key: breadcrumbCategory.id,
            label: breadcrumbCategory.title,
            value: breadcrumbCategory.id,
          },
        },
        {
          name: 'otherCategories',
          value: otherCategories.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          })),
        },
        {
          name: 'tags',
          value: tags.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          })),
        },
        {
          name: 'productType',
          value: productType && {
            key: productType,
            label: localizeProductType(productType).label,
            value: productType,
          },
        },
        {
          name: 'variableAttributes',
          value: variableAttributes.map(({ id, name }) => ({
            key: id,
            label: name,
            value: id,
          })),
        },
        ...baseAttributes.map(({ id, type, selectedValues }) => {
          const isCheckBox = type === 'CheckBox';

          const relatedCategories = otherCategories.filter(
            ({ relatedAttribute }) => relatedAttribute?.id === id,
          );
          const relatedValues = relatedCategories.flatMap(
            ({ relatedAttribute }) => relatedAttribute?.selectedValues ?? [],
          );
          const currentSelectedValues = generateUniqueElements([
            ...relatedValues,
            ...selectedValues,
          ]);

          return {
            name: `base-attribute-${id}`,
            value: isCheckBox
              ? currentSelectedValues
              : currentSelectedValues.at(0),
          };
        }),
        ...pictures.map(({ id }, index) => ({
          name: `file-index-${id}`,
          value: index + 1,
        })),
        ...documents.map(({ id }, index) => ({
          name: `file-index-${id}`,
          value: index + 1,
        })),
        ...[...pictures, ...documents].map(({ id, subProductId }) => {
          const isValidSubProduct = subProducts.some(
            subProduct => subProduct.id === subProductId,
          );

          return {
            name: `file-subProduct-${id}`,
            value: isValidSubProduct ? subProductId : undefined,
          };
        }),
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:flex-1 *:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:max-w-[70%] xl:last:*:max-w-[30%]'
        gap='large'
      >
        <ProductFormTabs />
        <ProductFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { ProductForm };
